import { type Control, useWatch } from 'react-hook-form';
import { Stack, Grid, Typography, Box, Divider } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import { WEEK_DAY_SHORT_NAMES } from '#apps/client-web/features/audit-form-background/constants';
import { type EditAuditFormBackgroundData } from '#apps/client-web/pages/audit-form-background/types';

import { isNil } from '#packages/data-collection/libs/lodash';

export type OperationHoursDetailsProps = {
  control: Control<EditAuditFormBackgroundData>;
};

export const OperationHoursDetails = ({
  control,
}: OperationHoursDetailsProps) => {
  const { general: { operationSchedule, operationHoursDescription } = {} } =
    useWatch({ control });

  const sortedDays = [...(operationSchedule ?? [])]
    .filter((entry) => entry.isUnavailable || entry.startTime || entry.endTime)
    .sort((a, b) => a.weekDay! - b.weekDay!);

  return (
    <Box sx={{ width: '100%' }}>
      <Stack spacing={1}>
        <Typography
          component="div"
          variant="body2"
          color="textSecondary"
          sx={{ mb: 0.5 }}>
          <Grid container alignItems="center">
            <AccessTimeIcon fontSize="inherit" />
            <span>&nbsp;Operation Hours</span>
          </Grid>
        </Typography>
        {sortedDays.length === 0 ? (
          <Grid container justifyContent="space-between">
            <Typography component="span" variant="body2">
              Schedule:
            </Typography>
            <Typography component="span" variant="body2" color="textDisabled">
              —
            </Typography>
          </Grid>
        ) : (
          sortedDays.map((entry) => (
            <Grid key={entry.weekDay} container justifyContent="space-between">
              <Typography component="span" variant="body2">
                {WEEK_DAY_SHORT_NAMES[entry.weekDay!]}:
              </Typography>
              <Typography
                component="span"
                variant="body2"
                color={entry.isUnavailable ? 'textDisabled' : 'textPrimary'}>
                {entry.isUnavailable
                  ? 'Unavailable'
                  : `${entry.startTime ?? '?'} – ${entry.endTime ?? '?'}`}
              </Typography>
            </Grid>
          ))
        )}
        <Grid container justifyContent="space-between">
          <Typography component="span" variant="body2">
            Description:
          </Typography>
          <Typography
            component="span"
            variant="body2"
            color={
              isNil(operationHoursDescription) ? 'textDisabled' : 'textPrimary'
            }>
            {operationHoursDescription ?? '—'}
          </Typography>
        </Grid>
        <Divider />
      </Stack>
    </Box>
  );
};
